import usePosOrders from "../../../services/Pos/usePosOrders";
import PosKOTPrint from "./PosKOTPrint";
import POSPrint from "./PosPrint";

export default function CashPaymentModal({
  cart = [],
  total = 0,
  paidAmount = 0,
  discount = 0,
  delivery = 0,
  orderType,
  printData,
  gstType = "excl",
  cgstRate: passedCgstRate = 2.5,
  sgstRate: passedSgstRate = 2.5,
  totalTaxRate: passedTotalTaxRate = 5,
  customerMobile = "",
  reciptDetails,
  onClose,
  isOpen,
}) {
  const { getOrders } = usePosOrders();
  const mobileNumber =
    typeof customerMobile === "object" ? customerMobile.mobile : customerMobile;

  const toNum = (value) => {
    const n = Number(value || 0);
    return Number.isFinite(n) ? n : 0;
  };

  const passedTotalAmount = toNum(total);

  const actualPaidAmount = toNum(
    paidAmount ||
      printData?.ui_paid_amount ||
      printData?.receivedAmount ||
      printData?.received_amount ||
      printData?.pay_amount ||
      passedTotalAmount,
  );

  const rawDueAmount = toNum(
    printData?.ui_due_amount ??
      printData?.due_amount ??
      printData?.dueAmount ??
      printData?.remaining_amount ??
      printData?.balance_amount ??
      0,
  );

  const paymentStatus =
    printData?.ui_payment_status ||
    printData?.payment_status ||
    printData?.paymentStatus ||
    "unpaid";

  const normalizedPaymentStatus = String(paymentStatus).toLowerCase().trim();

  const dueAmount = Number(Math.max(rawDueAmount, 0).toFixed(2));

  const formattedPaymentStatus = String(paymentStatus)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

  const safeJsonParse = (value) => {
    if (!value) return value;
    if (typeof value !== "string") return value;

    try {
      const parsed = JSON.parse(value);

      if (typeof parsed === "string") {
        try {
          return JSON.parse(parsed);
        } catch {
          return parsed;
        }
      }

      return parsed;
    } catch {
      return value;
    }
  };

  const getRawCustomization = (item) => {
    if (item?.customization !== undefined) {
      return safeJsonParse(item.customization);
    }

    if (item?.customizations !== undefined) {
      return safeJsonParse(item.customizations);
    }

    return [];
  };

  const getComboCustomization = (item) => {
    const directLineType = String(item?.lineType || item?.line_type || "")
      .trim()
      .toUpperCase();

    if (directLineType === "COMBO" || item?.comboId || item?.combo_id) {
      return item;
    }

    const parsed = getRawCustomization(item);

    if (
      parsed &&
      !Array.isArray(parsed) &&
      typeof parsed === "object" &&
      String(parsed?.lineType || parsed?.line_type || "")
        .trim()
        .toUpperCase() === "COMBO"
    ) {
      return parsed;
    }

    if (Array.isArray(parsed)) {
      return (
        parsed.find(
          (entry) =>
            String(entry?.lineType || entry?.line_type || "")
              .trim()
              .toUpperCase() === "COMBO",
        ) || null
      );
    }

    return null;
  };

  const parseCustomizations = (item) => {
    const parsed = getRawCustomization(item);

    if (Array.isArray(parsed)) return parsed;

    // Combo customization is an object, not a regular customization array.
    if (getComboCustomization(item)) return [];

    if (parsed && typeof parsed === "object") return [parsed];

    return [];
  };

  const getCustomizationText = (item) => {
    return parseCustomizations(item)
      .flatMap((c) => {
        const options =
          c?.options ||
          c?.option ||
          c?.addons ||
          c?.values ||
          c?.value ||
          c?.selectedOptions ||
          [];

        return Array.isArray(options) ? options : [options];
      })
      .filter(Boolean)
      .join(", ");
  };

  const getComboSelectedGroups = (item) => {
    const combo = getComboCustomization(item);
    return Array.isArray(combo?.selectedGroups) ? combo.selectedGroups : [];
  };

  const getItemPrice = (item) => {
    const combo = getComboCustomization(item);

    return toNum(
      item?.price ||
        item?.item_price ||
        item?.itemPrice ||
        combo?.comboPrice ||
        0,
    );
  };

  const getItemQty = (item) => toNum(item?.qty || item?.quantity || 1) || 1;

  const cartSubtotal = (Array.isArray(cart) ? cart : []).reduce((sum, item) => {
    return sum + getItemPrice(item) * getItemQty(item);
  }, 0);

  const apiSubtotal = toNum(
    printData?.subtotal ||
      printData?.subTotal ||
      printData?.subtotal_amount ||
      printData?.subtotalAmount,
  );

  // Same payment summary rule:
  // live combo/cart amount is the base amount.
  const subtotal =
    cartSubtotal > 0
      ? cartSubtotal
      : apiSubtotal > 0
        ? apiSubtotal
        : passedTotalAmount;

  const discountAmount = toNum(
    discount ||
      printData?.discount_amount ||
      printData?.discountAmount ||
      printData?.discount ||
      0,
  );

  const taxableAmount = Math.max(subtotal - discountAmount, 0);

  const normalizedGstType = String(gstType || "excl")
    .toLowerCase()
    .trim();

  const isInclusiveGst =
    normalizedGstType === "incl" || normalizedGstType === "inclusive";

  const cgstRate = toNum(passedCgstRate);
  const sgstRate = toNum(passedSgstRate);

  const combinedSplitRate = cgstRate + sgstRate;

  const totalTaxRate =
    toNum(passedTotalTaxRate) > 0
      ? toNum(passedTotalTaxRate)
      : combinedSplitRate > 0
        ? combinedSplitRate
        : 5;

  const getTaxAmounts = (amount) => {
    const safeAmount = Math.max(toNum(amount), 0);

    if (isInclusiveGst) {
      const includedTotalTax =
        totalTaxRate > 0
          ? safeAmount - safeAmount / (1 + totalTaxRate / 100)
          : 0;

      const splitRate =
        combinedSplitRate > 0 ? combinedSplitRate : totalTaxRate;

      if (splitRate <= 0) {
        return { cgst: 0, sgst: 0 };
      }

      return {
        cgst:
          combinedSplitRate > 0
            ? includedTotalTax * (cgstRate / combinedSplitRate)
            : includedTotalTax / 2,
        sgst:
          combinedSplitRate > 0
            ? includedTotalTax * (sgstRate / combinedSplitRate)
            : includedTotalTax / 2,
      };
    }

    return {
      cgst: (safeAmount * cgstRate) / 100,
      sgst: (safeAmount * sgstRate) / 100,
    };
  };

  const taxAmounts = getTaxAmounts(taxableAmount);
  const cgstAmount = taxAmounts.cgst;
  const sgstAmount = taxAmounts.sgst;

  const getCustomerAddress = () => {
    const rawAddress =
      printData?.address_line ||
      printData?.address ||
      printData?.customer_address ||
      printData?.billingAddress ||
      printData?.billing_address ||
      printData?.address_json ||
      reciptDetails?.address ||
      "";

    if (!rawAddress) return "";

    if (typeof rawAddress === "string") {
      try {
        const parsed = JSON.parse(rawAddress);

        if (typeof parsed === "string") return parsed;

        return [
          parsed?.address,
          parsed?.addressLine,
          parsed?.address_line,
          parsed?.line1,
          parsed?.line2,
          parsed?.city,
          parsed?.state,
          parsed?.pincode,
          parsed?.pinCode,
        ]
          .filter(Boolean)
          .join(", ");
      } catch {
        return rawAddress;
      }
    }

    if (typeof rawAddress === "object") {
      return [
        rawAddress?.address,
        rawAddress?.addressLine,
        rawAddress?.address_line,
        rawAddress?.line1,
        rawAddress?.line2,
        rawAddress?.city,
        rawAddress?.state,
        rawAddress?.pincode,
        rawAddress?.pinCode,
      ]
        .filter(Boolean)
        .join(", ");
    }

    return "";
  };

  const customerAddress = getCustomerAddress();

  const deliveryChargeAmount = Number(
    delivery ||
      printData?.delivery_fee ||
      printData?.deliveryCharges ||
      printData?.deliveryAmount ||
      printData?.delivery_charge ||
      0,
  );

  const calculatedOrderTotal = isInclusiveGst
    ? taxableAmount + deliveryChargeAmount
    : taxableAmount + cgstAmount + sgstAmount + deliveryChargeAmount;

  const orderTotalAmount = Number(calculatedOrderTotal.toFixed(2));

  const printableCart = (Array.isArray(cart) ? cart : []).map((item) => ({
    ...item,
    qty: getItemQty(item),
    price: getItemPrice(item),
    customizations: parseCustomizations(item),
  }));

  const apiReceiptItems =
    Array.isArray(printData?.items) && printData.items.length > 0
      ? printData.items.map((item) => ({
          ...item,
          qty: getItemQty(item),
          price: getItemPrice(item),
          customizations: parseCustomizations(item),
        }))
      : [];

  // Prefer the live cart whenever available. It contains the complete combo
  // customization/selectedGroups that can be missing from printData.
  const receiptItems =
    printableCart.length > 0 ? printableCart : apiReceiptItems;

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 montserrat-font-1"
        // onClick={() => onClose()}
      >
        <div
          className="bg-white rounded-2xl flex overflow-hidden w-full max-w-2xl montserrat-font-1 shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* ── Receipt (left) ── */}
          <div className="flex-1 p-8 border-r-2 border-dashed border-gray-200">
            {/* Success icon */}
            <div className="w-14 h-14 rounded-full bg-emerald-500 flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-7 h-7 text-white"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.5}
                strokeLinecap="round"
                strokeLinejoin="round"
                viewBox="0 0 24 24"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>

            <h2 className="text-center text-2xl font-semibold text-emerald-600 mb-0.5">
              Order Placed
            </h2>
            <p className="text-center text-sm text-gray-400 mb-6">
              Successfully
            </p>

            <p className="text-sm text-gray-500 mb-1">
              Customer Mobile :{" "}
              <span className="text-gray-800 font-medium">{mobileNumber}</span>
            </p>
            <p className="text-sm text-gray-500 mb-4">
              Order Type :{" "}
              <span className="text-gray-800 font-medium">{orderType}</span>
            </p>
            {customerAddress && (
              <p className="text-sm text-gray-500 mb-4 leading-relaxed">
                Address :{" "}
                <span className="text-gray-800 font-medium break-words">
                  {customerAddress}
                </span>
              </p>
            )}
            {/* Items table */}
            <table className="w-full text-xs border-collapse mb-4">
              <thead>
                <tr className="bg-gray-50">
                  {["#", "Item Name", "Qty", "Unit Price (₹)", "Total (₹)"].map(
                    (h) => (
                      <th
                        key={h}
                        className="border border-gray-200 px-2 py-2 text-left font-medium text-gray-800"
                      >
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {receiptItems.map((item, i) => {
                  const qty = Number(item.qty || item.quantity || 1);

                  const price = Number(
                    item.price ||
                      item.item_price ||
                      item.itemPrice ||
                      item.selectedPrice?.price ||
                      0,
                  );

                  const rowTotal =
                    Number(item.total_price || item.totalPrice || 0) ||
                    price * qty;

                  return (
                    <tr key={item.item_id || item.id || i}>
                      <td className="border border-gray-200 px-2 py-2">
                        {i + 1}
                      </td>

                      <td className="border border-gray-200 px-2 py-2">
                        <div className="font-bold text-[14px] text-gray-800">
                          {getComboCustomization(item)?.comboName ||
                            item.item_name ||
                            item.productName ||
                            "Item"}
                        </div>

                        {getComboCustomization(item)?.comboDescription && (
                          <div className="mt-1 text-[11px] leading-snug text-gray-500">
                            {getComboCustomization(item).comboDescription}
                          </div>
                        )}

                        {getComboSelectedGroups(item).length > 0 && (
                          <div className="mt-1.5 space-y-1">
                            {getComboSelectedGroups(item).map(
                              (group, groupIndex) => (
                                <div
                                  key={`${group?.optionGroupId || groupIndex}-${groupIndex}`}
                                  className="text-[11px] leading-snug text-gray-600"
                                >
                                  <span className="font-semibold text-gray-700">
                                    {group?.groupTitle || "Selection"}:
                                  </span>{" "}
                                  {(Array.isArray(group?.items)
                                    ? group.items
                                    : []
                                  )
                                    .map((selectedItem) => {
                                      const name =
                                        selectedItem?.productName ||
                                        selectedItem?.actualName ||
                                        selectedItem?.itemName ||
                                        "Item";
                                      const size =
                                        selectedItem?.sizeWords ||
                                        selectedItem?.variantName ||
                                        "";

                                      return size ? `${name} (${size})` : name;
                                    })
                                    .join(", ")}
                                </div>
                              ),
                            )}
                          </div>
                        )}

                        {!getComboCustomization(item) &&
                          getCustomizationText(item) && (
                            <div className="text-[14px] text-gray-700 mt-1 leading-snug">
                              Custom: {getCustomizationText(item)}
                            </div>
                          )}
                      </td>

                      <td className="border border-gray-200 px-2 py-2 text-center">
                        {qty}
                      </td>

                      <td className="border border-gray-200 px-2 py-2 text-right">
                        ₹{price.toFixed(2)}
                      </td>

                      <td className="border border-gray-200 px-2 py-2 text-right">
                        ₹{rowTotal.toFixed(2)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Summary */}
            <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1 montserrat-font-1">
              <div className="flex justify-between text-gray-500">
                <span>Subtotal:</span>
                <span>₹{subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-gray-500">
                <span>Discount Amount:</span>
                <span>₹{discountAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-gray-500">
                <span>CGST{isInclusiveGst ? " (Included)" : ""}:</span>
                <span>₹{cgstAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-gray-500">
                <span>SGST{isInclusiveGst ? " (Included)" : ""}:</span>
                <span>₹{sgstAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-gray-500">
                <span>Delivery Charges:</span>
                <span>₹ {deliveryChargeAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-semibold text-emerald-600 border-t border-gray-200 pt-2 mt-1">
                <span>Total:</span>
                <span>₹{orderTotalAmount.toFixed(2)}</span>
              </div>

              <div className="flex justify-between font-semibold">
                <span>Payment Status:</span>

                <span
                  className={`px-2 py-0.5 rounded-full text-xs ${
                    normalizedPaymentStatus === "paid"
                      ? "bg-emerald-100 text-emerald-700"
                      : normalizedPaymentStatus === "partially_paid"
                        ? "bg-yellow-100 text-yellow-700"
                        : "bg-red-100 text-red-600"
                  }`}
                >
                  {formattedPaymentStatus}
                </span>
              </div>

              <div className="flex justify-between font-semibold text-blue-500">
                <span>Pay Amount:</span>
                <span>₹{actualPaidAmount.toFixed(2)}</span>
              </div>

              <div className="flex justify-between font-semibold text-red-500">
                <span>Due Amount:</span>
                <span>₹{dueAmount.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* ── Action buttons (right) ── */}
          <div className="w-44 flex flex-col items-center justify-center  montserrat-font-1 gap-2.5 p-4">
            {["PRINT", "KITCHEN PRINT", "HOME"].map((label) => (
              <button
                key={label}
                onClick={async () => {
                  if (label === "PRINT") {
                    if (!printData?.orderid) {
                      console.error("printData not ready");
                      return;
                    }

                    // await processPrintOrder(printData?.orderid);

                    document.getElementById("pos-print-btn")?.click();

                    const t = new Date().toISOString().split("T")[0];
                    await getOrders({ fromDate: t, toDate: t });
                  } else if (label === "KITCHEN PRINT") {
                    if (!printData?.orderid) {
                      console.error("Kitchen print data not ready");
                      return;
                    }

                    setTimeout(() => {
                      document.getElementById("pos-kitchen-print-btn")?.click();
                    }, 150);

                    const t = new Date().toISOString().split("T")[0];
                    await getOrders({ fromDate: t, toDate: t });
                  } else if (label === "HOME") {
                    onClose("home");
                  }
                }}
                className="w-full py-5 rounded-lg border montserrat-font-1 border-gray-200 bg-gray-50 hover:bg-gray-100 mt-5 
      active:scale-95 text-sm font-bold tracking-wide text-gray-900 transition-all"
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: "none" }}>
          <POSPrint
            cart={printableCart}
            totalPrice={orderTotalAmount}
            orderType={orderType}
            mobile={customerMobile}
            reciptDetails={reciptDetails}
            apiResponsePrint={printData}
          />
        </div>

        <div style={{ display: "none" }}>
          <PosKOTPrint
            cart={printableCart}
            totalPrice={orderTotalAmount}
            orderType={orderType}
            mobile={customerMobile}
            reciptDetails={reciptDetails}
            apiResponsePrint={printData}
          />
        </div>
      </div>
    </>
  );
}
